import React from "react";
import "./OnuDetailsModal.css";

// Shows the OLT/ONU monitoring service's lookup result for a customer's
// MAC address — onu_details.php looks the MAC up from the broadband
// provider first, then queries the shared OLT/ONU service with it.
//
// Props:
//   open     — whether the modal is visible
//   onClose  — () => void
//   loading  — true while onu_details.php is in flight
//   error    — error message string, or null
//   data     — the onu_details.php success payload:
//              { mac, found, best, live, matches }
const OnuDetailsModal = ({ open, onClose, loading, error, data }) => {
  if (!open) return null;

  const best = data?.best || null;
  const matches = Array.isArray(data?.matches) ? data.matches : [];

  const fmtDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fmtPower = (value) => {
    if (value === null || value === undefined) return "--";
    return `${value} dBm`;
  };

  // Rough, generally-accepted GPON/EPON optical power bands — not
  // provider-specific, just enough to flag an obviously bad reading at
  // a glance. Anything missing just renders neutral (no badge color).
  const powerLevel = (value) => {
    if (value === null || value === undefined) return "";
    if (value <= -27) return "power-critical";
    if (value <= -24) return "power-warning";
    return "power-good";
  };

  return (
    <div className="onu-modal-overlay" onClick={onClose}>
      <div className="onu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="onu-modal-header">
          <h3>📡 ONU Details</h3>
          <button className="onu-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="onu-modal-body">
          {loading && (
            <div className="onu-modal-state">
              <span className="onu-modal-spinner"></span>
              <p>Looking up ONU details…</p>
            </div>
          )}

          {!loading && error && (
            <div className="onu-modal-state">
              <div className="onu-modal-state-icon">⚠️</div>
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && !data?.found && (
            <div className="onu-modal-state">
              <div className="onu-modal-state-icon">🔍</div>
              <p>No ONU record found for this connection's MAC address.</p>
              {data?.mac && (
                <p className="onu-modal-mac-tried">Looked up: {data.mac}</p>
              )}
            </div>
          )}

          {!loading && !error && data?.found && best && (
            <>
              <div className="onu-modal-status-row">
                <span
                  className={`onu-status-badge ${
                    best.onu_status === "online" ? "online" : "offline"
                  }`}
                >
                  <span className="status-dot"></span>
                  {best.onu_status || "unknown"}
                </span>
                {best.stale && (
                  <span className="onu-status-badge stale">stale reading</span>
                )}
              </div>

              <div className="onu-modal-grid">
                <div className="onu-modal-card">
                  <h4>OLT / Port</h4>
                  <div className="onu-modal-row">
                    <span>Device</span>
                    <strong>{best.device_name || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Device IP</span>
                    <strong>{best.device_ip || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>PON Port</span>
                    <strong>{best.port_name || best.pon || "--"}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU No / ID</span>
                    <strong>
                      {best.onu_no ?? "--"} / {best.onu_id ?? "--"}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Distance</span>
                    <strong>
                      {best.distance_m !== null && best.distance_m !== undefined
                        ? `${best.distance_m} m`
                        : "--"}
                    </strong>
                  </div>
                </div>

                <div className="onu-modal-card">
                  <h4>Signal</h4>
                  <div className="onu-modal-row">
                    <span>ONU Rx Power</span>
                    <strong className={powerLevel(best.onu_rx_power)}>
                      {fmtPower(best.onu_rx_power)}
                    </strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>ONU Tx Power</span>
                    <strong>{fmtPower(best.onu_tx_power)}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>OLT Rx Power</span>
                    <strong>{fmtPower(best.olt_rx_power)}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>MAC</span>
                    <strong>{best.mac || data.mac || "--"}</strong>
                  </div>
                </div>

                {/* <div className="onu-modal-card">
                  <h4>Activity</h4>
                  <div className="onu-modal-row">
                    <span>First Seen</span>
                    <strong>{fmtDate(best.first_seen)}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Last Seen</span>
                    <strong>{fmtDate(best.last_seen)}</strong>
                  </div>
                  <div className="onu-modal-row">
                    <span>Source</span>
                    <strong>{best.source || "--"}</strong>
                  </div>
                </div> */}
              </div>

              {matches.length > 1 && (
                <div className="onu-modal-matches">
                  <h4>All matches ({matches.length})</h4>
                  <table className="onu-modal-table">
                    <thead>
                      <tr>
                        <th>Port</th>
                        <th>VLAN</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th>Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((m, i) => (
                        <tr key={i}>
                          <td>{m.port_name || m.pon || "--"}</td>
                          <td>{m.vlan ?? "--"}</td>
                          <td>{m.onu_status || "--"}</td>
                          <td>{m.source || "--"}</td>
                          <td>{fmtDate(m.last_seen)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnuDetailsModal;
